import { apiPost } from './client';
import type { SmsCascadePrefetchCache } from './smsCascadePrefetch';
import { extractSmsCatalog } from './smsProviderCatalog';

export type SmsCabinetCheckUser = {
  id?: number;
  name?: string;
  balance?: number;
  currency?: string;
  currency_id?: number;
  email?: string;
  role?: string;
};

/** Ответ POST …/front-api/check (прокси /api/sms/check). */
export type SmsCabinetCheckResponse = {
  token?: string;
  user?: SmsCabinetCheckUser;
  [key: string]: unknown;
};

function pickRecord(obj: unknown): Record<string, unknown> | null {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    return obj as Record<string, unknown>;
  }
  return null;
}

function parseBalanceField(raw: unknown): number | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** user из тела check: вложенность data.user, плоский объект с balance или стандартный user. */
export function userFromSmsCheckResponse(raw: SmsCabinetCheckResponse | null | undefined): SmsCabinetCheckUser | undefined {
  const r = pickRecord(raw);
  if (!r) return undefined;

  const tryUser = (o: Record<string, unknown> | null): SmsCabinetCheckUser | undefined => {
    if (!o) return undefined;
    const bal = parseBalanceField(o.balance ?? o.balance_money ?? o.account_balance);
    if (bal === undefined) return undefined;
    const currency =
      typeof o.currency === 'string'
        ? o.currency
        : typeof o.currency_code === 'string'
          ? o.currency_code
          : undefined;
    const out: SmsCabinetCheckUser = { balance: bal };
    if (currency?.trim()) out.currency = currency.trim();
    return out;
  };

  const u1 = tryUser(pickRecord(r.user));
  if (u1) return u1;
  const data = pickRecord(r.data);
  const u2 = tryUser(data ? pickRecord(data.user) : null);
  if (u2) return u2;
  const u3 = tryUser(data);
  if (u3) return u3;

  return tryUser(r);
}

export function formatNumericBalance(balance: number, currency?: string | null): string {
  const formatted = balance.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
  const cur = (currency || '').trim();
  return cur ? `${formatted}\u00A0${cur}` : formatted;
}

/** Текст для шапки: check (user / countBalance в корне), иначе префетч GET /sms/create. */
export function resolveHeaderBalanceLine(
  check: SmsCabinetCheckResponse | null | undefined,
  prefetchCache: SmsCascadePrefetchCache
): string | null {
  const fromUser = formatCabinetBalance(userFromSmsCheckResponse(check));
  if (fromUser) return fromUser;

  const root = pickRecord(check);
  if (root) {
    const cb = parseBalanceField(root.countBalance);
    if (cb !== undefined) {
      const cur =
        typeof root.currency === 'string'
          ? root.currency
          : typeof root.currencySymbol === 'string'
            ? root.currencySymbol
            : null;
      return formatNumericBalance(cb, cur);
    }
  }

  const cat = extractSmsCatalog(prefetchCache);
  if (cat.countBalance != null && Number.isFinite(cat.countBalance)) {
    return formatNumericBalance(cat.countBalance);
  }
  return null;
}

/**
 * Проверка живой сессии SMS-сервиса (JWT в Authorization = token в теле на стороне прокси).
 * 503 если SMS_API_BASE_URL не задан — в этом случае возвращает null (локальная разработка).
 */
export async function fetchSmsCabinetCheck(): Promise<SmsCabinetCheckResponse | null> {
  try {
    return await apiPost<SmsCabinetCheckResponse>('/api/sms/check', {});
  } catch (e: unknown) {
    const st = (e as Error & { status?: number }).status;
    if (st === 503) return null;
    throw e;
  }
}

export function formatCabinetBalance(user: SmsCabinetCheckUser | undefined): string | null {
  if (!user || user.balance === undefined || user.balance === null || Number.isNaN(Number(user.balance))) {
    return null;
  }
  const bal = typeof user.balance === 'number' ? user.balance : Number(user.balance);
  const formatted = Number.isFinite(bal)
    ? bal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 })
    : String(user.balance);
  const cur = (user.currency || '').trim();
  return cur ? `${formatted}\u00A0${cur}` : formatted;
}
